import { useRef, useState } from "react";
import { Image as ImageIcon, Video as VideoIcon, Trash2, UploadCloud, AlertTriangle, Loader2 } from "lucide-react";
import api, { tokenStore } from "@/lib/api";
import { uploadEvidence } from "@/lib/cctvUpload";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CCTV } from "@/constants/testIds";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fileUrl(ev) {
    return `${BACKEND}${ev.url}?auth=${tokenStore.getAccess()}`;
}

const MB = 1024 * 1024;

export default function EvidenceSection({
    findingId, evidence, editable, canDelete, onChanged, t: tProp, hrVideoMb = 150,
}) {
    const i18n = useI18n();
    const t = tProp || i18n.t;
    const inputRef = useRef(null);
    const [pct, setPct] = useState(null);
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);
    const [busy, setBusy] = useState(false);

    // A clip the HR platform cannot play. We still store it — the full
    // recording belongs here — but the escalation would reach HR as
    // "held in the QC system", visible and not watchable, so say it now.
    const tooBigForHr = (ev) => ev.kind === "video" && (ev.size || 0) > hrVideoMb * MB;

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setError(null);
        const isVideo = (file.type || "").startsWith("video/");
        setWarning(isVideo && file.size > hrVideoMb * MB
            ? t("cctv_hr_clip_too_big").replace("{mb}", hrVideoMb)
            : null);
        setBusy(true);
        setPct(0);
        try {
            await uploadEvidence(findingId, file, { onProgress: setPct });
            toast.success(t("cctv_saved"));
            onChanged && (await onChanged());
        } catch (err) {
            const detail = err?.response?.data?.detail || t("cctv_upload_failed");
            setError(detail);            // LOUD, inline
            toast.error(detail);          // LOUD, toast
        } finally {
            setBusy(false);
            setPct(null);
        }
    };

    const del = async (eid) => {
        try {
            await api.delete(`/cctv/findings/${findingId}/evidence/${eid}`);
            onChanged && (await onChanged());
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("error_generic"));
        }
    };

    return (
        <div data-testid={CCTV.evidence} className="space-y-3">
            <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[color:var(--ink)]">
                    {t("cctv_evidence")} <span className="text-xs font-normal text-[color:var(--ink-faint)]">· {t("cctv_evidence_hint")}</span>
                </p>
                {editable ? (
                    <>
                        <input
                            ref={inputRef}
                            data-testid={CCTV.evidenceInput}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
                            className="hidden"
                            onChange={onPick}
                        />
                        <Button type="button" size="sm" variant="soft" className="gap-1.5" disabled={busy}
                            onClick={() => inputRef.current?.click()}>
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
                            {t("cctv_add_evidence")}
                        </Button>
                    </>
                ) : null}
            </div>

            {editable ? (
                <p className="text-[11px] text-[color:var(--ink-faint)]">{t("cctv_max_note")} · {t("cctv_upload_note_timeout")}</p>
            ) : null}

            {pct !== null ? (
                <div data-testid={CCTV.uploadProgress} className="space-y-1">
                    <Progress value={pct} className="h-2" />
                    <p className="text-[11px] text-[color:var(--ink-muted)]">{t("cctv_uploading")} {pct}%</p>
                </div>
            ) : null}

            {warning ? (
                <div data-testid={CCTV.hrClipWarning}
                    className="flex items-start gap-2 rounded-lg border border-[color:var(--warning)] bg-[color:var(--warning-soft)] p-2.5 text-xs"
                    style={{ color: "#B45309" }}>
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{warning}</span>
                </div>
            ) : null}

            {error ? (
                <div data-testid={CCTV.uploadError} className="flex items-start gap-2 rounded-lg border border-[color:var(--danger)] bg-[color:var(--danger-soft)] p-2.5 text-xs text-[color:var(--danger)]">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{error}</span>
                </div>
            ) : null}

            {(!evidence || evidence.length === 0) ? (
                <p className="text-sm italic text-[color:var(--ink-faint)]">{t("cctv_no_evidence")}</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {evidence.map((ev) => (
                        <div key={ev.id} data-testid={CCTV.evidenceItem(ev.id)} className="group relative rounded-xl overflow-hidden border border-[color:var(--border-soft)] bg-black/5">
                            {ev.kind === "video" ? (
                                <video src={fileUrl(ev)} controls className="w-full h-36 object-cover bg-black" />
                            ) : (
                                <img src={fileUrl(ev)} alt={ev.original_filename || "evidence"} className="w-full h-36 object-cover" />
                            )}
                            <span className="absolute top-1.5 start-1.5 inline-flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                {ev.kind === "video" ? <VideoIcon className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                                {ev.kind === "video" ? t("cctv_video") : t("cctv_photo")}
                            </span>
                            {tooBigForHr(ev) ? (
                                <span data-testid={CCTV.evidenceHrFlag(ev.id)}
                                    className="absolute bottom-1.5 start-1.5 inline-flex items-center gap-1 rounded-md bg-[#B45309] px-1.5 py-0.5 text-[10px] font-semibold text-white"
                                    title={t("cctv_hr_clip_too_big").replace("{mb}", hrVideoMb)}>
                                    <AlertTriangle className="h-3 w-3" />
                                    {t("cctv_hr_not_playable")}
                                </span>
                            ) : null}
                            {canDelete ? (
                                <button
                                    type="button"
                                    data-testid={CCTV.evidenceDelete(ev.id)}
                                    onClick={() => del(ev.id)}
                                    className="absolute top-1.5 end-1.5 rounded-md bg-[color:var(--danger)] p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    title={t("cctv_delete_evidence")}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
